import { Close, DeleteSweep } from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface DeduplicateDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  companyId: string;
  listId: string;
  columns: Array<any>;
  onSuccess?: () => void;
  onBackgroundTaskCreated?: () => void;
}

const DeduplicateDialog: React.FC<DeduplicateDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  columns,
  onSuccess,
  onBackgroundTaskCreated,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<"delete" | "merge">("delete");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "info" | "warning";
    message: string;
  } | null>(null);

  // Filter out action columns and get regular columns only
  const regularColumns = columns.filter((col) => col.type !== "action");

  const handleColumnToggle = (columnName: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnName)) {
        return prev.filter((name) => name !== columnName);
      } else {
        return [...prev, columnName];
      }
    });
  };

  const handleDeduplicate = async () => {
    setStatusMessage(null);

    if (selectedColumns.length === 0) {
      setStatusMessage({
        type: "error",
        message: "Please select at least one column to deduplicate by",
      });
      return;
    }

    setLoading(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/lists/${listId}/deduplicate`,
        {
          companyId,
          columnNames: selectedColumns,
          strategy: strategy,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Background task started successfully
      setStatusMessage({
        type: "success",
        message:
          response.data.message ||
          "Deduplication started in background. You can monitor progress in the background tasks panel below.",
      });

      if (onBackgroundTaskCreated) {
        onBackgroundTaskCreated();
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      const errorMessage = parseError(err);
      setStatusMessage({
        type: "error",
        message: `Failed to start deduplication: ${errorMessage}`,
      });
      eventListener({ type: "SET_ERROR", error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedColumns([]);
    setStrategy("delete");
    setStatusMessage(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteSweep />
          <Typography variant="h6">Deduplicate Records</Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {statusMessage && (
          <Alert severity={statusMessage.type} sx={{ mb: 2 }}>
            {statusMessage.message}
          </Alert>
        )}

        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Select the columns to use for finding duplicates. Rows with identical
          values in all selected columns will be considered duplicates.
        </Typography>

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            Deduplication Strategy:
          </Typography>
          <RadioGroup
            value={strategy}
            onChange={(e) => setStrategy(e.target.value as "delete" | "merge")}
          >
            <FormControlLabel
              value="delete"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    Delete Duplicates
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Keep the oldest record and delete all duplicates
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="merge"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    Merge Duplicates
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Keep the oldest record and fill in empty fields from
                    duplicates before deleting them
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          Select Columns to Check:
        </Typography>

        <List sx={{ maxHeight: 300, overflow: "auto" }}>
          {regularColumns.map((column) => (
            <ListItem
              key={column.field}
              dense
              button
              onClick={() => handleColumnToggle(column.name)}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectedColumns.includes(column.name)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={column.name} />
            </ListItem>
          ))}
        </List>

        {selectedColumns.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Selected columns: {selectedColumns.join(", ")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <LoadingButton onClick={handleClose} disabled={loading}>
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={handleDeduplicate}
          loading={loading}
          variant="contained"
          color="primary"
          disabled={selectedColumns.length === 0}
        >
          Deduplicate
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeduplicateDialog;
